/*
 * Product-card cart stepper.
 * After a product is in the cart, the card's "Add to cart" button is replaced with
 * [bin/minus]  N in cart  [+]. Uses the theme's standard cart event so the header
 * count and cart drawer stay in sync, and re-syncs whenever the cart changes elsewhere.
 */
import { CartLinesUpdateEvent, StandardEvents } from 'https://cdn.shopify.com/storefront/standard-events.js';

const TRASH =
  '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 5.5h13"/><path d="M8 5.5V3.8h4v1.7"/><path d="M5.3 5.5l.8 10.7h7.8l.8-10.7"/><path d="M8.3 8.5v5M11.7 8.5v5"/></svg>';
const MINUS =
  '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4.5 10h11"/></svg>';

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

function quantitiesFrom(cart) {
  const q = {};
  (cart?.items || []).forEach((i) => {
    q[String(i.variant_id)] = (q[String(i.variant_id)] || 0) + i.quantity;
  });
  return q;
}

function render(qtyByVariant) {
  document.querySelectorAll('.card-actions[data-variant-id]').forEach((wrap) => {
    const qty = qtyByVariant[wrap.dataset.variantId] || 0;
    const stepper = wrap.querySelector('.card-qty');
    if (!stepper) return;
    wrap.classList.toggle('card-actions--in-cart', qty > 0);
    stepper.hidden = qty <= 0;
    const count = stepper.querySelector('.card-qty__count');
    if (count) count.textContent = String(qty);
    const minus = stepper.querySelector('.card-qty__minus');
    if (minus) {
      minus.innerHTML = qty <= 1 ? TRASH : MINUS;
      minus.setAttribute('aria-label', qty <= 1 ? 'Remove from cart' : 'Decrease quantity');
    }
  });
}

async function fetchCart() {
  const r = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return r.json();
}

async function changeQuantity(source, variantId, newQty) {
  const current = await fetchCart();
  const line = (current.items || []).find((i) => String(i.variant_id) === String(variantId));
  if (!line) return render(quantitiesFrom(current));

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'product',
      lines: [{ id: line.key, quantity: newQty }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: line.key, quantity: newQty, sections: cartSectionIds() }),
    });
    const cart = await res.json();
    if (!res.ok || cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'card-actions',
        itemCount: cart.item_count,
        sections: cart.sections,
        didError: false,
      },
    });
    render(quantitiesFrom(cart));
  } catch (err) {
    deferred.reject(err);
    render(quantitiesFrom(await fetchCart()));
  }
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest?.('.card-qty__btn');
  if (!btn) return;
  event.preventDefault();
  event.stopPropagation();
  const wrap = btn.closest('.card-actions[data-variant-id]');
  if (!wrap || wrap.dataset.busy === '1') return;
  const count = parseInt(wrap.querySelector('.card-qty__count')?.textContent || '0', 10);
  const next = btn.classList.contains('card-qty__plus') ? count + 1 : Math.max(0, count - 1);
  wrap.dataset.busy = '1';
  changeQuantity(btn, wrap.dataset.variantId, next).finally(() => {
    wrap.dataset.busy = '';
  });
}, true);

// Re-sync all cards whenever the cart changes anywhere (card add, drawer, product page).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => (detail?.items ? { items: detail.items } : fetchCart()))
    .then((cart) => render(quantitiesFrom(cart)))
    .catch(() => {});
});

// Initial sync (covers cached pages / back navigation).
fetchCart().then((cart) => render(quantitiesFrom(cart))).catch(() => {});
window.addEventListener('pageshow', (e) => {
  if (e.persisted) fetchCart().then((cart) => render(quantitiesFrom(cart))).catch(() => {});
});
